/** The Graph tab: rail, canvas, inspector — the whole model on one screen. */

import { useEffect, useState } from 'react'
import { useToast } from '@/shared/components/toast'
import { Dock, DockMain, DockPanel, useDock } from '@/shared/components/dock/Dock'
import { Icon } from '@/shared/components/icons'
import { GraphCanvas } from './GraphCanvas'
import { nodeColour } from './palette'
import { NodeInspector } from './NodeInspector'
import { useNodeTypeMutations } from '../hooks'
import type { GraphModel } from '../types/brain.types'

export function GraphTab({ graphId, model }: { graphId: string; model: GraphModel }) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const { move } = useNodeTypeMutations(graphId)
  const toast = useToast()
  // The rail and the inspector are dock panels: either can be minimised to a
  // strip or maximised over the canvas. The inspector opens on a selection.
  const dock = useDock('brain.graph', { rail: 'open', inspector: 'min' })

  // A delete should not leave the inspector pointing at something that is not
  // there. Nothing selected is a legitimate state, though — it is what clicking
  // the empty canvas asks for — so this only ever clears, never picks.
  useEffect(() => {
    if (!selectedId) return
    const exists = model.node_types.some((n) => n.node_type_id === selectedId)
    if (!exists) setSelectedId(null)
  }, [model.node_types, selectedId])

  // Esc closes the inspector, the same key that closes every other panel in
  // the app. Bound on the document rather than the canvas: the selection can be
  // made by clicking the rail, and the focus is then not on the canvas at all.
  useEffect(() => {
    if (!selectedId) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedId(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [selectedId])

  const selected = model.node_types.find((n) => n.node_type_id === selectedId)
  const select = (id: string | null) => {
    setSelectedId(id)
    if (id) dock.reveal('inspector')
  }

  return (
    <Dock
      className="cp-graphpane"
      left={dock.state.rail}
      right={dock.state.inspector}
      sizes={{ left: 210, right: 320 }}
    >
      <DockPanel
        side="left"
        title="Node types"
        icon={<Icon.list />}
        state={dock.state.rail}
        onState={(st) => dock.set('rail', st)}
        summary={<span className="badge badge-neutral">{model.node_types.length}</span>}
      >
      <div className="brain-rail">
        {model.node_types.length === 0 && <p className="small faint">None yet.</p>}
        {model.node_types.map((node) => (
          <button
            key={node.node_type_id}
            className="railitem"
            data-on={node.node_type_id === selectedId}
            onClick={() => select(node.node_type_id)}
          >
            {/* The same colour the canvas draws it in, so the rail reads as an
                index of the picture rather than as a separate list. */}
            <span
              className="cat-dot"
              style={{ background: nodeColour(node.node_type_id) }}
              aria-hidden="true"
            />
            <span className="rail-node-label"><span className="truncate">{node.display_name || node.key}</span><small>{node.category}</small></span>
            <span className="count">{(node.record_count ?? 0).toLocaleString()}</span>
          </button>
        ))}
      </div>
      </DockPanel>

      <DockMain>
      <div className="cp-canvaswrap">
        <GraphCanvas
          nodeTypes={model.node_types}
          edgeTypes={model.edge_types}
          selectedId={selectedId}
          onSelect={select}
          editable={editing}
          onMove={(nodeTypeId, x, y) => move.mutate({ id: nodeTypeId, x, y })}
          overlay={
            <>
              {/* A control, so it sits apart from the key rather than inside it. */}
              <div className="cp-canvastool">
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    setEditing((v) => !v)
                    if (!editing) toast.show('Drag a node type to move it. The layout is saved.')
                  }}
                >
                  {editing ? 'Done arranging' : 'Arrange'}
                </button>
              </div>
              {/* A key to the actual model rather than to three categories:
                  every type is its own colour now, so the legend has to name
                  them. Clicking one selects it, which makes the legend a way
                  to find a type on a crowded canvas. */}
              <div className="cp-legend">
                {model.node_types.map((node) => (
                  <button
                    key={node.node_type_id}
                    type="button"
                    className="cp-legenditem"
                    data-on={node.node_type_id === selectedId}
                    onClick={() => select(node.node_type_id)}
                  >
                    <span
                      className="cat-dot"
                      style={{ background: nodeColour(node.node_type_id) }}
                      aria-hidden="true"
                    />
                    {node.key}
                  </button>
                ))}
                <span className="faint">Record counts shown on each node</span>
              </div>
            </>
          }
        />
      </div>
      </DockMain>

      <DockPanel
        side="right"
        title={selected ? selected.key : 'Inspector'}
        icon={<Icon.sliders />}
        state={dock.state.inspector}
        onState={(st) => dock.set('inspector', st)}
      >
        <NodeInspector graphId={graphId} node={selected} />
      </DockPanel>
    </Dock>
  )
}
